import { useEffect, useRef, type RefObject } from 'react'

// All floating layers render into one portal root, so they stack above page content
// regardless of where they're declared (the #390 stacking bugs came from z-index inside
// stacking contexts)
export function getOverlayRoot(): HTMLElement {
  let root = document.getElementById('ui-overlay-root')
  if (!root) {
    root = document.createElement('div')
    root.id = 'ui-overlay-root'
    root.style.position = 'relative'
    root.style.zIndex = '1000'
    document.body.appendChild(root)
  }
  return root
}

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])', 'select:not([disabled])',
  'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',')

export function focusableWithin(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(el => !el.hasAttribute('hidden'))
}

// Open overlays, innermost last - only the top one handles Escape and traps focus
const stack: Array<RefObject<HTMLElement>> = []

/**
 * Dialog behaviour: Escape closes, Tab stays inside, body scroll is locked, and focus
 * moves in on open and back to the opener on close.
 */
export function useDialogBehavior(open: boolean, onClose: () => void, containerRef: RefObject<HTMLElement>, initialFocusRef?: RefObject<HTMLElement>) {
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return
    const opener = document.activeElement as HTMLElement | null
    stack.push(containerRef)
    const isTop = () => stack[stack.length - 1] === containerRef

    const container = containerRef.current
    const target = initialFocusRef?.current || (container && focusableWithin(container)[0]) || container
    target?.focus()

    function onKeyDown(e: KeyboardEvent) {
      if (!isTop()) return
      if (e.key === 'Escape') {
        e.stopPropagation()
        onCloseRef.current()
        return
      }
      if (e.key === 'Tab' && containerRef.current) {
        const items = focusableWithin(containerRef.current)
        if (items.length === 0) {
          e.preventDefault()
          return
        }
        const first = items[0]
        const last = items[items.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      const index = stack.indexOf(containerRef)
      if (index >= 0) stack.splice(index, 1)
      if (stack.length === 0) document.body.style.overflow = previousOverflow
      opener?.focus?.()
    }
  }, [open])
}
